/**
 * comms.ts (COMMS ARC, 2026-08-13) — hooks for the automation engine + outbox.
 *
 * The model: every automated (or manual template) send flows through ONE table,
 * pegasus_outbox. While org_setting comms_live=false, rows land HELD — visible
 * "would have sent" traffic the office reviews and tunes. Nothing reaches a
 * customer. Held rows never auto-send at go-live; only rows created after the
 * flip go out. The cron edge function `comms-run` evaluates triggers + sends.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// ---------- types ----------
export interface CommsAutomation {
  key: string;
  label: string;
  description: string | null;
  enabled: boolean;
  channel: "sms" | "email";
  template_sms: string | null;
  template_email_subject: string | null;
  template_email_body: string | null;
  config: Record<string, unknown>;
  sort: number;
}
export interface OutboxRow {
  id: string;
  automation_key: string;
  custno: string | null;
  locno: string | null;
  customer_name: string | null;
  channel: "sms" | "email";
  to_address: string;
  subject: string | null;
  body: string;
  trigger_kind: string | null;
  trigger_ref: string | null;
  scheduled_for: string;
  status: "held" | "queued" | "sent" | "failed" | "suppressed" | "cancelled";
  status_detail: string | null;
  sent_at: string | null;
  created_by: string | null;
  created_at: string;
}
export interface CommsPref {
  custno: string;
  no_sms: boolean;
  no_email: boolean;
  no_review_request: boolean;
  /** PAPER-INVOICE-01: office prints & mails invoices — billing email is
   *  auto-suppressed (DB trigger) and finalized invoices land in the To-Mail
   *  queue on the Invoices view. */
  paper_invoice: boolean;
  /** COMMS-UX-01: account-level marketing-email stop (newsletters/offers);
   *  transactional email unaffected. Mirrored to consent by the sync trigger. */
  no_marketing_email: boolean;
  note: string | null;
}

// ---------- the master gate ----------
export function useCommsLive() {
  return useQuery({
    queryKey: ["comms_live"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<boolean> => {
      const { data } = await supabase.from("org_setting").select("value").eq("key", "comms_live").maybeSingle();
      return data?.value === true || data?.value === "true";
    },
  });
}

export function useQuietHours() {
  return useQuery({
    queryKey: ["comms_quiet_hours"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<{ start: number; end: number }> => {
      const { data } = await supabase.from("org_setting").select("key, value").in("key", ["comms_quiet_start", "comms_quiet_end"]);
      const m = new Map((data ?? []).map((r) => [r.key, r.value]));
      return { start: Number(m.get("comms_quiet_start") ?? 8), end: Number(m.get("comms_quiet_end") ?? 20) };
    },
  });
}
export function useSaveQuietHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { start: number; end: number }) => {
      const rows = [
        { key: "comms_quiet_start", value: v.start },
        { key: "comms_quiet_end", value: v.end },
      ];
      const { error } = await supabase.from("org_setting").upsert(rows, { onConflict: "key" });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["comms_quiet_hours"] }),
  });
}

// ---------- automations ----------
export function useCommsAutomations() {
  return useQuery({
    queryKey: ["comms_automations"],
    staleTime: 15 * 1000,
    queryFn: async (): Promise<CommsAutomation[]> => {
      const { data, error } = await supabase.from("pegasus_comms_automation").select("*").order("sort");
      if (error) throw error;
      return (data ?? []) as CommsAutomation[];
    },
  });
}
export function useSaveAutomation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: Partial<CommsAutomation> & { key: string }) => {
      const { data: userData } = await supabase.auth.getUser();
      const { key, ...patch } = v;
      const { error } = await supabase
        .from("pegasus_comms_automation")
        .update({ ...patch, updated_at: new Date().toISOString(), updated_by: userData?.user?.email ?? null })
        .eq("key", key);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["comms_automations"] }),
  });
}

// ---------- the outbox ----------
export function useOutbox(filter: { status?: string; automation?: string }) {
  return useQuery({
    queryKey: ["comms_outbox", filter],
    staleTime: 10 * 1000,
    refetchInterval: 30 * 1000,
    queryFn: async (): Promise<OutboxRow[]> => {
      let q = supabase.from("pegasus_outbox").select("*").order("created_at", { ascending: false }).limit(200);
      if (filter.status && filter.status !== "all") q = q.eq("status", filter.status);
      if (filter.automation && filter.automation !== "all") q = q.eq("automation_key", filter.automation);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as OutboxRow[];
    },
  });
}
export function useOutboxCounts() {
  return useQuery({
    queryKey: ["comms_outbox_counts"],
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
    queryFn: async (): Promise<Record<string, number>> => {
      const out: Record<string, number> = {};
      for (const status of ["held", "queued", "sent", "failed", "suppressed"]) {
        const { count } = await supabase.from("pegasus_outbox").select("id", { count: "exact", head: true }).eq("status", status);
        out[status] = count ?? 0;
      }
      return out;
    },
  });
}
export function useCancelOutbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("pegasus_outbox")
        .update({ status: "cancelled", status_detail: `cancelled by ${userData?.user?.email ?? "office"}`, updated_at: new Date().toISOString() })
        .eq("id", id)
        .in("status", ["held", "queued"]); // only unsent rows can be cancelled
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["comms_outbox"] });
      qc.invalidateQueries({ queryKey: ["comms_outbox_counts"] });
    },
  });
}

/**
 * Manual enqueue (on-the-way text, invoice email…) — the SAME honest path the
 * automations use: live → queued (cron sends within ~10 min), testing → held.
 */
export function useEnqueueOutbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      automation_key: string;
      dedupe_key: string;
      custno: string | null;
      locno?: string | null;
      customer_name?: string | null;
      channel: "sms" | "email";
      to_address: string;
      subject?: string | null;
      body: string;
      trigger_kind?: string;
      trigger_ref?: string;
    }): Promise<"queued" | "held"> => {
      const { data: gate } = await supabase.from("org_setting").select("value").eq("key", "comms_live").maybeSingle();
      const live = gate?.value === true || gate?.value === "true";
      const { data: userData } = await supabase.auth.getUser();
      const status = live ? "queued" : "held";
      const { error } = await supabase.from("pegasus_outbox").insert({
        automation_key: v.automation_key,
        dedupe_key: v.dedupe_key,
        custno: v.custno,
        locno: v.locno ?? null,
        customer_name: v.customer_name ?? null,
        channel: v.channel,
        to_address: v.to_address,
        subject: v.subject ?? null,
        body: v.body,
        trigger_kind: v.trigger_kind ?? null,
        trigger_ref: v.trigger_ref ?? null,
        scheduled_for: new Date().toISOString(),
        status,
        created_by: userData?.user?.email ?? null,
      });
      if (error) throw error;
      return status;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["comms_outbox"] });
      qc.invalidateQueries({ queryKey: ["comms_outbox_counts"] });
      // INVOICE-SENT-01: the invoice's "Sent to customer" chip + Today's
      // Invoices To Send read the outbox — refresh them the moment a send lands.
      qc.invalidateQueries({ queryKey: ["invoice_send_status"] });
      qc.invalidateQueries({ queryKey: ["unsent_billable_invoices"] });
      qc.invalidateQueries({ queryKey: ["customer_comms"] });
    },
  });
}

// ---------- per-customer preferences ----------
export function useCommsPref(custno: string | null | undefined) {
  return useQuery({
    queryKey: ["comms_pref", custno],
    enabled: Boolean(custno),
    staleTime: 30 * 1000,
    queryFn: async (): Promise<CommsPref | null> => {
      const { data } = await supabase.from("pegasus_comms_pref").select("*").eq("custno", custno as string).maybeSingle();
      return (data ?? null) as CommsPref | null;
    },
  });
}
export function useSaveCommsPref() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { custno: string } & Partial<Omit<CommsPref, "custno">>) => {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_comms_pref").upsert(
        { ...v, updated_at: new Date().toISOString(), updated_by: userData?.user?.email ?? null },
        { onConflict: "custno" },
      );
      if (error) throw error;
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["comms_pref", v.custno] }),
  });
}

// ---------- token helpers (client mirrors of comms-run's) ----------
export function fillTokens(s: string, map: Record<string, string>): string {
  return s.replace(/\{\{(\w+)\}\}/g, (_m, k) => map[k] ?? "");
}
export function firstNameOf(name: string | null | undefined): string {
  if (!name) return "there";
  let n = name.trim().replace(/\s+\d{4,}$/, "");
  if (n.includes(",")) {
    const i = n.indexOf(",");
    n = `${n.slice(i + 1).trim()} ${n.slice(0, i).trim()}`;
  }
  const first = (n.split(/\s+/)[0] ?? "").replace(/[^A-Za-z'-]/g, "");
  if (first.length < 2) return "there";
  return first[0].toUpperCase() + first.slice(1).toLowerCase();
}

export const AUTOMATION_LABEL: Record<string, string> = {
  appt_confirm: "Booking confirmation",
  appt_reminder: "Day-before reminder",
  appt_otw: "On-the-way text",
  review_request: "Review request",
  invoice_reminder: "Invoice reminder",
  invoice_send: "Invoice email",
};
